import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/** @jsxImportSource @emotion/react */
import { Dialog, DialogBody, DialogFooter } from '@blueprintjs/core';
import { css } from '@emotion/react';
import { Molecule } from 'openchemlib/full';
import { TopicMolecule } from 'openchemlib-utils';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { StructureEditor } from 'react-ocl/full';
import { useOnOff } from 'react-science/ui';
import { useDispatch } from '../context/DispatchContext';
import ActionButtons from '../elements/ActionButtons';
function MoleculeStructureEditorModal(props) {
    const { onClose = () => null, selectedMolecule, floatMoleculeOnSave = false, isOpen = false, } = props;
    const initialMolfile = selectedMolecule?.molfile;
    const initialEnhancedMolfile = useMemo(() => {
        if (!initialMolfile) {
            return null;
        }
        // Add mapNo to molfile, so we can remap diaIDs after edition.
        const molecule = Molecule.fromMolfile(initialMolfile);
        const topicMolecule = new TopicMolecule(molecule);
        topicMolecule.ensureMapNo();
        const newMolfile = topicMolecule.toMolfile({ version: 3 });
        return { molfile: newMolfile, topicMolecule };
    }, [initialMolfile]);
    const [molfile, setMolfile] = useState(null);
    const dispatch = useDispatch();
    useEffect(() => {
        if (initialEnhancedMolfile) {
            setMolfile(initialEnhancedMolfile.molfile);
        }
        else {
            setMolfile(null);
        }
    }, [initialEnhancedMolfile]);
    const cb = useCallback((newMolfile, molecule) => {
        if (molecule.getAllAtoms() === 0) {
            setMolfile(null);
        }
        else {
            setMolfile(newMolfile);
        }
    }, [setMolfile]);
    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);
    const handleSave = useCallback(() => {
        if (molfile) {
            if (selectedMolecule && initialEnhancedMolfile) {
                const { id, label } = selectedMolecule;
                const editedMolecule = Molecule.fromMolfile(molfile);
                const mappings = initialEnhancedMolfile.topicMolecule.getDiaIDsMapping(editedMolecule);
                dispatch({
                    type: 'SET_MOLECULE',
                    payload: {
                        molfile,
                        id,
                        label,
                        mappings,
                    },
                });
                onClose('replace');
            }
            else {
                dispatch({
                    type: 'ADD_MOLECULE',
                    payload: {
                        molfile,
                        floatMoleculeOnSave,
                    },
                });
                onClose('new');
            }
        }
    }, [
        molfile,
        selectedMolecule,
        initialEnhancedMolfile,
        dispatch,
        floatMoleculeOnSave,
        onClose,
    ]);
    return (_jsxs(Dialog, { isOpen: isOpen, onClose: handleClose, style: { width: 710 }, children: [_jsx(DialogBody, { css: css `
          background-color: white;
        `, children: _jsx(StructureEditor, { initialMolfile: initialEnhancedMolfile?.molfile, svgMenu: true, fragment: false, onChange: cb }) }), _jsx(DialogFooter, { children: _jsx(ActionButtons, { style: { flexDirection: 'row-reverse', margin: 0 }, onDone: handleSave, doneLabel: "Save", onCancel: () => {
                        onClose();
                    } }) })] }));
}
export function useMoleculeEditor(floatMoleculeOnSave = false) {
    const [isOpen, openModal, closeModal] = useOnOff(false);
    const [molecule, setMolecule] = useState();
    const modal = (_jsx(MoleculeStructureEditorModal, { selectedMolecule: molecule, floatMoleculeOnSave: floatMoleculeOnSave, onClose: closeModal, isOpen: isOpen }));
    const openMoleculeEditor = useCallback((molecule) => {
        setMolecule(molecule);
        openModal();
    }, [openModal]);
    return { modal, openMoleculeEditor };
}
